import { useEffect } from "react";
import { useState } from "react";
import { useModal } from "../../../contexts/ModalContext";

import PurchaseModal from "../PurchaseModal";
import { CharacterOptionButton } from "./CharacterOptionButton";

import { useCustomization } from "../../../contexts/CustomizationContext";

import {
    type ItemDatabaseMethod,
    type ItemData,
    ItemFilter,
    AvatarItemKey,
} from "@mythic/pixl";

import "./CharacterOptions.css";
import { usePixl } from "../../../contexts/PixlContext";

interface Props {
    category: number;
    onSelection: (val: number) => void;
    allowNull?: boolean;
}
export function CharacterOptions({ category, onSelection, allowNull }: Props) {
    // buttonData contains the filtered list of category items.
    const [buttonData, setButtonData] = useState<ItemData[] | null>(null);

    // Used to display the PurchaseModal
    const showModal = useModal()?.showModal;

    // Customization data
    const { customization, purchases, setPurchases, categoryMap } =
        useCustomization();

    const pixlDb = usePixl()?.pixlDb;

    // Sprite generator and customization data is ready.
    useEffect(() => {
        // If not ready or if customization is not populated
        // abort this run of useEffect
        if (!customization || !pixlDb) return;

        // Get the data for the user's head
        const head = pixlDb.getItem("head", customization.head?.id);

        // Get the data for the user's body
        const body = pixlDb.getItem("body", customization.body?.id);

        // Create the head/body filter. This will be
        // used to only display items the user currently
        // has access to. (i.e. remove eyes/mouth with wiggler head)
        const filter = {
            requiredHeadStyle: head.style,
            requiredBodyStyle: body.style,
        } as ItemFilter;

        // Get the category data
        const categoryData = categoryMap[category];
        if (!categoryData) return;

        // Using the categoryData, execute the requisite SpriteGenerator method to
        // get the filtered data.
        const data = (
            pixlDb[categoryData.method as ItemDatabaseMethod] as (
                filter: ItemFilter,
            ) => ItemData[]
        )(filter);
        setButtonData(data);
    }, [category, pixlDb, customization, purchases]);

    // When the user purchases an item
    const onPurchaseHandler = (
        result: any,
        categoryData?: { key: string; method: string; allowNull?: boolean },
        item?: ItemData,
    ) => {
        if (!categoryData || !item) return;

        // result is data returned from the server.
        if (result.status === "ok") {
            // If the result is ok, the user successfully purchased
            // the item they inteded, update purchases.
            const newPurchases = [...purchases];

            // Update the purchases with the given item.
            // This is done in good faith of the result. This
            // data is not sent back to the server, so equipping
            // an item the user doesn't own will currently function
            // but their account still won't own the item.
            newPurchases.push({
                category: categoryData.key,
                id: item.id,
                purchase_date: 0,
            });

            // Update customization, causing a redraw.
            setPurchases(newPurchases);
        }
    };

    return (
        <div className="options-container">
            {allowNull && (
                <CharacterOptionButton
                    onClick={() => {
                        onSelection(-1);
                    }}
                />
            )}
            {//JSON.stringify(buttonData)
            buttonData?.map((b, i) => {
                if (!customization) return;

                // Check if the user owns the item.
                const owned = purchases.find((p) => p.id === b.id)
                    ? true
                    : false;

                // If the item is not for sale and the user doesn't already own it
                // omit the item from the list of options.
                if (b.price === -1 && !owned) return null;

                // Get the id of the current item
                const categoryData = categoryMap[category];
                const currentId = categoryData
                    ? customization[categoryData.key as AvatarItemKey]?.id
                    : "";

                // Compare that to the id of the item being drawn
                const isCurrent = b.id === currentId;

                const val = {
                    file: `${b.file}`,
                    owned,
                    isCurrent,
                    price: b.price,
                    iconFrame: b.iconFrame,
                };

                return (
                    <CharacterOptionButton
                        key={b.id}
                        {...val}
                        onClick={() => {
                            if (b.price === 0 || owned) onSelection(i);
                            else if (showModal)
                                showModal(
                                    <PurchaseModal
                                        item={b}
                                        category={categoryData?.key ?? ""}
                                        onPurchase={(result) => {
                                            onPurchaseHandler(
                                                result,
                                                categoryData,
                                                b,
                                            );
                                            onSelection(i);
                                        }}
                                        onTry={(item: ItemData) => {
                                            // The user is trying the item on
                                            onSelection(i);
                                        }}
                                    />,
                                );
                        }}
                    />
                );
            })}
        </div>
    );
}
